import { useEffect } from 'react'

/**
 * Scroll-reveal for elements marked `.wow`, paired with animate.css.
 *
 * Three details here are load-bearing and easy to break:
 *
 *  - The marker class added on reveal is `animated`, which matches **no** rule in
 *    animate.css 4.1.1 and is therefore inert. The visible animation comes from
 *    `.animate__fadeInUp` (animation-name) plus this system's own
 *    `.wow { animation-duration: 1s !important }`. Keep the class anyway: it is a
 *    useful "has been revealed" hook for tests and future styling.
 *  - A pending box is hidden with inline `visibility: hidden` and
 *    `animation-name: none`, and the cached animation-name is restored on reveal.
 *    Setting `display` instead would collapse layout and shift the page.
 *  - No markup currently uses `data-wow-delay` / `data-wow-duration`, so there is no
 *    per-element timing support. Add it here if markup ever needs it.
 *
 * Call once per route: the observer is rebuilt whenever `deps` change, which is
 * how reveals re-arm after a client-side navigation.
 */
export function useReveal(deps: readonly unknown[] = []): void {
  useEffect(() => {
    const boxes = Array.from(document.querySelectorAll<HTMLElement>('.wow'))
    if (boxes.length === 0) return

    // Respect the user's motion preference rather than hiding content behind an
    // animation they have asked not to see.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      for (const box of boxes) {
        box.style.visibility = 'visible'
        box.classList.add('animated')
      }
      return
    }

    const cached = new WeakMap<HTMLElement, string>()
    for (const box of boxes) {
      cached.set(box, getComputedStyle(box).animationName)
      box.style.visibility = 'hidden'
      box.style.animationName = 'none'
    }

    const reveal = (box: HTMLElement) => {
      box.style.visibility = 'visible'
      box.style.animationName = cached.get(box) ?? ''
      box.classList.add('animated')
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          reveal(entry.target as HTMLElement)
          observer.unobserve(entry.target)
        }
      },
      // Offset 0 — a box reveals as soon as any part of it enters the viewport.
      { threshold: 0 },
    )

    for (const box of boxes) observer.observe(box)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}
